import React, { useMemo } from 'react';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from '../../lib/charts/highcharts';
import ChartFrame from './ChartFrame';
import { SECTOR_LABELS, SPEED_BINS } from '../../lib/charts/history';

export const ROSE_COLORS = [1, 2, 3, 4, 5, 6].map((i) => `var(--chart-rose-${i})`);

/**
 * Polar stacked-column wind rose over the last hour (ROSE_WINDOW_MIN). 6 speed bins ×
 * 16 sectors, always zero-filled so the axes stay aligned. Bin colours are
 * the chart-local ROSE_COLORS ramp (var(--chart-rose-1..6), theme-aware).
 * tickmarkPlacement 'on' + startOnTick false centres the cardinal labels on
 * the bars.
 */
export function buildRoseSeries(cells, binLabel) {
    return SPEED_BINS.map((bin) => ({
        name: binLabel(bin.label),
        type: 'column',
        data: SECTOR_LABELS.map((direction) => {
            const cell = cells.find((c) => c.direction === direction && c.bin === bin.label);
            return { y: cell ? cell.percentage : 0, custom: { count: cell ? cell.count : 0 } };
        }),
    }));
}

const WindRoseChart = ({ cells, total, binLabel, tooltipText, height = 320, compactLegend = false, compact = false, emptyMessage }) => {
    const options = useMemo(() => (compact ? {
        // Compact "Wind now" glyph: bars only, no labels, no legend, no tooltip.
        chart: { polar: true, type: 'column', height, width: height, backgroundColor: 'transparent', margin: 0, spacing: [0, 0, 0, 0] },
        colors: ROSE_COLORS,
        title: { text: undefined },
        pane: { size: '100%' },
        xAxis: { categories: [...SECTOR_LABELS], tickmarkPlacement: 'on', startOnTick: false, labels: { enabled: false }, lineWidth: 0, gridLineWidth: 1 },
        yAxis: { min: 0, endOnTick: false, labels: { enabled: false }, title: { text: undefined }, reversedStacks: false, gridLineWidth: 1, tickAmount: 3 },
        plotOptions: { column: { stacking: 'normal', groupPadding: 0, pointPadding: 0.1, borderWidth: 0, pointPlacement: 'on', shadow: false }, series: { animation: { duration: 500 }, enableMouseTracking: false } },
        legend: { enabled: false },
        // followTouchMove:false — with it on, Highcharts sets touch-action:none on polar (xy-nearest) charts and the page can't scroll over the rose.
        tooltip: { enabled: false, followTouchMove: false },
        accessibility: { description: 'Wind rose: direction distribution over the last hour' },
        series: buildRoseSeries(cells, binLabel),
    } : {
        // Tight spacing + compact legend: the pane is sized from the plot area, so every px of chrome shrinks the rose.
        chart: { polar: true, type: 'column', height, backgroundColor: 'transparent', spacing: [4, 4, 4, 4] },
        // Chart-local ramp: global `colors` are not re-applied to live charts on theme swaps.
        colors: ROSE_COLORS,
        title: { text: undefined },
        // legend below (compactLegend) leaves the full width to the rose; legend-right needs a smaller pane.
        // 84% is the most the 380 px column fits before WNW/ENE labels clip (measured live at height 390).
        pane: { size: compactLegend ? '84%' : '74%' },
        // formatter hides the stray closing tick ("16") Highcharts adds past the last category
        xAxis: { categories: [...SECTOR_LABELS], tickmarkPlacement: 'on', startOnTick: false, labels: { distance: 10, style: { fontSize: '11px' }, formatter() { return typeof this.value === 'string' ? this.value : ''; } } },
        yAxis: { min: 0, endOnTick: false, showLastLabel: true, labels: { formatter() { return this.value === 0 ? '' : `${this.value}%`; } }, title: { text: undefined }, reversedStacks: false },
        plotOptions: {
            // pointPadding 0.1 (Highcharts default) leaves a visible gap between neighbouring sectors, like the pre-v12 rose.
            column: { stacking: 'normal', groupPadding: 0, pointPadding: 0.1, borderWidth: 0, pointPlacement: 'on', shadow: false },
            series: { animation: { duration: 500 } },
        },
        legend: compactLegend
            ? { enabled: true, layout: 'horizontal', align: 'center', verticalAlign: 'bottom', margin: 6, padding: 0, itemMarginTop: 0, itemMarginBottom: 2, symbolPadding: 4, itemDistance: 12, itemStyle: { fontSize: '10px' } }
            : { enabled: true, layout: 'vertical', align: 'right', verticalAlign: 'top', y: 0 },
        tooltip: {
            useHTML: false,
            followTouchMove: false, // tap shows the tooltip; a finger drag must scroll the page (see compact note)
            formatter() {
                const direction = typeof this.x === 'number' ? SECTOR_LABELS[this.x] : this.x;
                const args = { direction, bin: this.series.name, percentage: Number(this.y) || 0, count: (this.point.options.custom || {}).count || 0, total };
                return tooltipText ? tooltipText(args) : `${args.direction} · ${args.bin} · ${args.percentage.toFixed(0)}% (${args.count}/${args.total})`;
            },
        },
        accessibility: { description: 'Wind rose: direction and speed distribution over the last hour' },
        series: buildRoseSeries(cells, binLabel),
    }), [cells, total, binLabel, tooltipText, height, compactLegend, compact]);

    return (
        <ChartFrame ariaLabel="Wind rose" height={height} width={compact ? height : undefined} empty={!cells || cells.length === 0 || total < 2} emptyMessage={emptyMessage}>
            <HighchartsReact highcharts={Highcharts} options={options} />
        </ChartFrame>
    );
};

export default WindRoseChart;
